import { locationHref, readLocation, writeLocation } from "../utils/appLocation";
import { Form } from "../components";
import Icon from "../components/Icon";
import { Toast, PageTitle, Btn, Card, ConfirmModal, Input, Modal, Pagination, Select, Spinner, TrechoToggle } from "../components";
import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../api";
import OrderSelectionTable from '../components/OrderSelectionTable';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import DateCalendarStrip from "../components/DateCalendarStrip";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { buildProgramPlanningContext, programSelectionReason } from "../utils/programPlanning";
import { buildDateKey, formatDisplayDate, parseDateKey } from "../utils/dateCalendar";
import { summarizePlanningRoute } from "../utils/pedidoPlanningSummary";
import { getPedidoOrigemLabel } from "../utils/tenantLabels";
import { buildPassengerSelectionList } from "../utils/passengerSelection";
import { useTenant } from "../context/TenantContext";
import { recordOperationalAction, recordUiError } from "../observability";
import MontarRotaPage from "./MontarRota";
import PlanningDispatchModal from "../components/PlanningDispatchModal";
import PlanningOrders from "../components/PlanningOrders";
import { sendPlanningVans, planningDispatchMessage } from "../utils/routeFlow";
import BacklogRotasPage from "./BacklogRotas";
import { groupOrdersIntoTrips } from "../utils/vanBoard";

const ROUTE_DRAFT_EDIT_STORAGE_KEY = "rotaflex_route_draft_edit_id";

const PER_PAGE = 20;
const today = () => buildDateKey(new Date());

const emptyForm = () => ({
  codigo: "",
  data: today(),
  horario: "",
  trecho: "",
  empresa_destino_id: "",
  motorista_responsavel_id: "",
  veiculo_responsavel_id: "",
  funcionario_ids: [],
  observacoes: "",
  is_teste: false,
});

const WORKSPACE_TABS = [
  { id: "a_fazer", label: "A fazer" },
  { id: "planejamento", label: "Em montagem" },
  { id: "operacao", label: "Em operação" },
];

const groupPedidosByLote = (items = []) => {
  const map = new Map();
  for (const item of items) {
    const key = item.rota_operacional_id ? `rota-${item.rota_operacional_id}` : `pedido-${item.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return [...map.entries()].map(([key, pedidos]) => ({
    key,
    pedidos,
    rotaId: pedidos[0]?.rota_operacional_id || null,
  }));
};

const formatDate = formatDisplayDate;
const readExpandedPlanning = (params) => new Set(
  (params.get("detalhes") || "").split(",").filter(key => /^(rota|pedido)-\d+$/.test(key))
);

export default function PedidosPage({
  pessoas,
  motoristas,
  veiculos,
  userRole,
  onOpenDocuments,
  initialNav = null,
  onConsumeNavigation,
}) {
  const { config } = useTenant();
  const params = readLocation().searchParams;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => Math.max(1, Number(params.get("pagina")) || 1));
  const [listRefresh,setListRefresh] = useState(0);
  const [expandedPlanning, setExpandedPlanning] = useState(() => readExpandedPlanning(params));
  const [loading, setLoading] = useState(true);
  const [loadError,setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [search, setSearch] = useState(() => params.get("busca") || "");
  const [query, setQuery] = useState(() => params.get("busca") || "");
  const [destinationFilter,setDestinationFilter] = useState(() => params.get("destino") || "");
  const [legFilter,setLegFilter] = useState(() => ["ida","volta"].includes(params.get("sentido")) ? params.get("sentido") : "");
  const [formDiscard,setFormDiscard] = useState(null);
  const formBaseline = useRef("");
  const loadVersion = useRef(0);
  const planningOpenVersion = useRef(0);
  const openSavedPlanningRef = useRef(null);
  const [operationLocationKey,setOperationLocationKey] = useState(0);
  const skipSearchDebounceRef = useRef(true);
  const [selectedDateKey, setSelectedDateKey] = useState(() => params.get("data") === "todas" ? "" : params.get("data") || buildDateKey(new Date()));
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [countsByDate, setCountsByDate] = useState({});
  // Mantém a seleção independente da página/busca atual. A listagem é
  // paginada no servidor, portanto derivar os selecionados de `items` fazia
  // pedidos já marcados desaparecerem da contagem ao trocar o filtro.
  const [selectedItemsById, setSelectedItemsById] = useState(() => new Map());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingPlannedOrder, setEditingPlannedOrder] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [passengerSearch, setPassengerSearch] = useState("");
  const [empresas, setEmpresas] = useState([]);
  const formDirty = modalOpen && JSON.stringify(form) !== formBaseline.current;
  const requestFormLeave = action => { if (saving) return; if (formDirty) setFormDiscard({ run:action }); else action(); };
  const closeForm = () => requestFormLeave(() => setModalOpen(false));
  useUnsavedChanges(formDirty, requestFormLeave);

  const [deleteItem, setDeleteItem] = useState(null);
  const [withdrawItem, setWithdrawItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [lastDispatchRouteId, setLastDispatchRouteId] = useState(null);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  const [mainTab, setMainTab] = useState(() => WORKSPACE_TABS.some(tab => tab.id === params.get("etapa")) ? params.get("etapa") : "a_fazer");
  const [workspace, setWorkspace] = useState("lista");
  const [planningContext, setPlanningContext] = useState(null);
  const [planningDispatchProgress, setPlanningDispatchProgress] = useState({});
  const [draftRoute, setDraftRoute] = useState(null);
  const [backlogRouteId, setBacklogRouteId] = useState(() => Number(params.get("rota")) || null);
  const [tabCounts, setTabCounts] = useState({ a_fazer: 0, planejamento: 0, operacao: 0 });
  const [highlightIds, setHighlightIds] = useState(() => new Set());
  const [operacaoCountsByDate, setOperacaoCountsByDate] = useState({});
  const [operacaoSearch, setOperacaoSearch] = useState(() => params.get("busca") || "");
  const [operacaoQuery, setOperacaoQuery] = useState(() => params.get("busca") || "");
  const skipOperacaoSearchDebounceRef = useRef(true);

  useEffect(() => {
    writeLocation({ page: 'pedidos', etapa: mainTab, data: selectedDateKey || 'todas', busca: mainTab === 'operacao' ? operacaoQuery : query,
      destino: destinationFilter, sentido: legFilter, pagina: page > 1 ? page : null }, { replace: true });
  }, [mainTab, selectedDateKey, query, operacaoQuery, destinationFilter, legFilter, page]);
  useEffect(() => {
    const restore = () => {
      const values = readLocation().searchParams;
      setExpandedPlanning(readExpandedPlanning(values));
      planningOpenVersion.current += 1; setPlanning(false);
      setBacklogRouteId(Number(values.get('rota')) || null); setOperationLocationKey(value => value + 1);
      setMainTab(WORKSPACE_TABS.some(tab => tab.id === values.get('etapa')) ? values.get('etapa') : 'a_fazer');
      setSelectedDateKey(values.get('data') === 'todas' ? '' : values.get('data') || buildDateKey(new Date()));
      setSearch(values.get('busca') || ''); setQuery(values.get('busca') || '');
      setOperacaoSearch(values.get('busca') || ''); setOperacaoQuery(values.get('busca') || '');
      setDestinationFilter(values.get('destino') || ''); setLegFilter(values.get('sentido') || '');
      setPage(Math.max(1, Number(values.get('pagina')) || 1));
      setWorkspace('lista'); setPlanningContext(null); setDraftRoute(null);
      if (Number(values.get('montagem')) > 0) void openSavedPlanningRef.current(Number(values.get('montagem')), null, false);
    };
    if (Number(readLocation().searchParams.get('montagem')) > 0) void openSavedPlanningRef.current(Number(readLocation().searchParams.get('montagem')), null, false);
    window.addEventListener('rotaflex:restore-location', restore);
    return () => { planningOpenVersion.current += 1; window.removeEventListener('rotaflex:restore-location', restore); };
  }, []);

  const showMessage = (message, type = "success") => {
    if (type === "error") {
      recordUiError({ message, page: "pedidos", source: "toast" });
    }
    setToast({ message, type });
  };

  const loadOperacaoCalendarCounts = async () => {
    try {
      const data = await apiFetch("/rotas?limit=1");
      setOperacaoCountsByDate(data?.counts_by_date && typeof data.counts_by_date === "object" ? data.counts_by_date : {});
    } catch {
      // ignora falha de contadores
    }
  };

  const loadTabCounts = async (dateKey = selectedDateKey) => {
    try {
      const params = new URLSearchParams({ limit: "1", include_tab_counts: "1" });
      if (dateKey) params.set("data", dateKey);
      const data = await apiFetch(`/programacoes?${params.toString()}`);
      if (data?.tab_counts) setTabCounts(data.tab_counts);
    } catch {
      // ignora falha de contadores
    }
  };

  const load = async (targetPage = page) => {
    const requestVersion = ++loadVersion.current;
    setLoadError("");
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(targetPage), limit: String(PER_PAGE) });
      if (query.trim()) params.set("q", query.trim());
      if (destinationFilter) params.set("empresa_destino_id",destinationFilter);
      if (legFilter) params.set("trecho",legFilter);
      if (selectedDateKey) params.set("data", selectedDateKey);
      if (mainTab === "a_fazer") params.set("status", "rascunho");
      if (mainTab === "planejamento") {
        params.set("rota_status", "planejamento");
        params.set("group_by", "planejamento");
      }
      const data = await apiFetch(`/programacoes?${params.toString()}`);
      if (requestVersion !== loadVersion.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setCountsByDate(data?.counts_by_date && typeof data.counts_by_date === "object" ? data.counts_by_date : {});
      await loadTabCounts(selectedDateKey);
    } catch (error) {
      if (requestVersion === loadVersion.current) {setLoadError(error.message || "Não foi possível carregar os pedidos.");setItems([]);setTotal(0);showMessage(error.message,"error");}
    } finally {
      if (requestVersion === loadVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    apiFetch("/empresas-destino").then((rows) => setEmpresas(Array.isArray(rows) ? rows : [])).catch(() => setEmpresas([]));
  }, []);

  useEffect(() => {
    if (skipSearchDebounceRef.current) {
      skipSearchDebounceRef.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadOperacaoCalendarCounts();
  }, []);

  useEffect(() => {
    if (skipOperacaoSearchDebounceRef.current) {
      skipOperacaoSearchDebounceRef.current = false;
      return undefined;
    }
    const timer = setTimeout(() => setOperacaoQuery(operacaoSearch), 300);
    return () => clearTimeout(timer);
  }, [operacaoSearch]);

  const activeCalendarCounts = mainTab === "operacao" ? operacaoCountsByDate : countsByDate;
  const calendarCountLabel = mainTab === "operacao"
    ? "van"
    : mainTab === "planejamento"
      ? "planejamento"
      : "pedido";
  const calendarCountLabelPlural = mainTab === "operacao"
    ? "vans"
    : mainTab === "planejamento"
      ? "em montagem"
      : "pedidos";

  useEffect(() => {
    loadTabCounts(selectedDateKey);
  }, [selectedDateKey]);

  useEffect(() => {
    if (mainTab === "operacao") return;
    load(page);
  }, [page, query, selectedDateKey, mainTab, destinationFilter, legFilter, listRefresh]);

  useEffect(() => {
    if (calendarAnchor || !Object.keys(activeCalendarCounts).length) return;
    const availableDates = Object.keys(activeCalendarCounts).sort();
    const todayKey = buildDateKey(new Date());
    const firstUpcoming = availableDates.find((dateKey) => dateKey >= todayKey);
    const anchorDate = parseDateKey(firstUpcoming || availableDates[0]) || new Date();
    setCalendarAnchor(anchorDate);
  }, [activeCalendarCounts, calendarAnchor]);

  useEffect(() => {
    if (!initialNav) return;
    if (!initialNav.planning && !initialNav.draftRoute) { planningOpenVersion.current += 1; setPlanning(false); setWorkspace("lista"); setPlanningContext(null); setDraftRoute(null); }
    if (initialNav.tab) setMainTab(initialNav.tab);
    if (initialNav.routeId) {
      setBacklogRouteId(initialNav.routeId);
      setMainTab("operacao");
    }
    if (initialNav.planning) {
      setPlanningContext(initialNav.planning);
      setDraftRoute(null);
      setWorkspace("planejar");
    }
    if (initialNav.draftRoute) {
      setDraftRoute(initialNav.draftRoute);
      setPlanningContext(null);
      setWorkspace("planejar");
    }
    if (initialNav.focusDate) {
      setSelectedDateKey(String(initialNav.focusDate).substring(0, 10));
    }
    if (Array.isArray(initialNav.highlightIds) && initialNav.highlightIds.length) {
      setHighlightIds(new Set(initialNav.highlightIds.map(Number)));
      setMainTab("a_fazer");
    }
    onConsumeNavigation?.();
  }, [initialNav, onConsumeNavigation]);

  useEffect(() => {
    if (!highlightIds.size) return undefined;
    const timer = setTimeout(() => setHighlightIds(new Set()), 12000);
    return () => clearTimeout(timer);
  }, [highlightIds]);

  const tabItems = useMemo(() => {
    if (mainTab === "a_fazer") {
      return items.filter((item) => item.status === "rascunho");
    }
    if (mainTab === "planejamento") {
      return items.filter((item) => item.rota_status === "planejamento" && Number(item.rota_total_grupos) > 0);
    }
    return [];
  }, [items, mainTab]);

  const loteGroups = useMemo(() => groupPedidosByLote(tabItems), [tabItems]);
  const togglePlanningDetails = (key) => {
    const next = new Set(expandedPlanning);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpandedPlanning(next);
    writeLocation({ detalhes: [...next].join(",") || null }, { replace: true });
  };
  const activeTab = WORKSPACE_TABS.find((tab) => tab.id === mainTab) || WORKSPACE_TABS[0];
  const activeSearch = mainTab === "operacao" ? operacaoSearch : search;
  const switchMainTab = (tabId) => {
    planningOpenVersion.current += 1;
    setPlanning(false);
    writeLocation({ etapa: tabId, pagina: null, montagem: null, busca: activeSearch || null });
    setSearch(activeSearch); setQuery(activeSearch);
    setOperacaoSearch(activeSearch); setOperacaoQuery(activeSearch);
    setMainTab(tabId);
    setPage(1);
  };
  const clearActiveSearch = () => {
    if (mainTab === "operacao") {
      setOperacaoSearch("");
      setOperacaoQuery("");
      return;
    }
    setSearch("");
    setQuery("");
  };

  const openPlanning = (context) => {
    localStorage.removeItem(ROUTE_DRAFT_EDIT_STORAGE_KEY);
    setPlanningContext(context);
    setDraftRoute(null);
    setWorkspace("planejar");
  };

  const handlePlanningBack = () => {
    planningOpenVersion.current += 1; setPlanning(false);
    writeLocation({ montagem: null }, { replace: true });
    localStorage.removeItem(ROUTE_DRAFT_EDIT_STORAGE_KEY);
    setWorkspace("lista");
    setPlanningContext(null);
    setDraftRoute(null);
    load(page);
  };

  const handlePlanningComplete = (result = {}) => {
    planningOpenVersion.current += 1; setPlanning(false);
    writeLocation({ montagem: null }, { replace: true });
    localStorage.removeItem(ROUTE_DRAFT_EDIT_STORAGE_KEY);
    setWorkspace("lista");
    setPlanningContext(null);
    setSelectedItemsById(new Map());
    setMainTab(result.routeId ? "operacao" : "planejamento");
    setListRefresh(value=>value+1);
    if (result.routeId) setBacklogRouteId(result.routeId);
    setPage(1);
    loadTabCounts(selectedDateKey);
    loadOperacaoCalendarCounts();
  };

  const handleEditRouteFromBacklog = (route) => {
    if (route?.id) {
      localStorage.setItem(ROUTE_DRAFT_EDIT_STORAGE_KEY, String(route.id));
    }
    setDraftRoute(route);
    setPlanningContext(null);
    setWorkspace("planejar");
  };
  const selectedItems = useMemo(
    () => [...selectedItemsById.values()],
    [selectedItemsById]
  );
  const visibleSelectedCount = useMemo(
    () => tabItems.filter((item) => selectedItemsById.has(Number(item.id))).length,
    [tabItems, selectedItemsById]
  );
  const hiddenSelectedCount = Math.max(0, selectedItems.length - visibleSelectedCount);
  const tripsToBuild = useMemo(() => (mainTab === "a_fazer" ? groupOrdersIntoTrips(tabItems) : []), [mainTab, tabItems]);
  const selectedCompanyId = String(form.empresa_destino_id || "");
  const selectedCompany = empresas.find((company) => String(company.id) === selectedCompanyId) || null;
  const availableDrivers = selectedCompany?.cliente_id
    ? motoristas.filter((driver) => String(driver.cliente_id || "") === String(selectedCompany.cliente_id))
    : motoristas;
  const availableVehicles = selectedCompany?.cliente_id
    ? veiculos.filter((vehicle) => String(vehicle.cliente_id || "") === String(selectedCompany.cliente_id))
    : veiculos;
  const availablePeople = useMemo(() => pessoas.filter((person) => (
    !selectedCompanyId || String(person.empresa_destino_id || "") === selectedCompanyId
  )), [pessoas, selectedCompanyId]);
  const filteredPeople = useMemo(() => buildPassengerSelectionList({
    people: availablePeople,
    selectedIds: form.funcionario_ids,
    search: passengerSearch,
  }), [availablePeople, form.funcionario_ids, passengerSearch]);

  const openNew = () => {
    const next = emptyForm();
    if (empresas.length === 1) next.empresa_destino_id = String(empresas[0].id);
    setForm(next);
    formBaseline.current = JSON.stringify(next);
    setEditingId(null);
    setEditingPlannedOrder(false);
    setPassengerSearch("");
    setModalOpen(true);
  };

  const openEdit = async (item) => {
    setSaving(true);
    try {
      const details = await apiFetch(`/programacoes/${item.id}?cliente_id=${item.cliente_id}`);
      if (details?.operacional && !details.operacional.pode_editar) {
        showMessage("Este pedido não pode mais ser editado.", "error");
        return;
      }
      const next = {
        codigo: details.codigo || "",
        data: String(details.data || "").substring(0, 10),
        horario: details.horario || "00:00",
        trecho: details.trecho || "volta",
        empresa_destino_id: String(details.empresa_destino_id || ""),
        motorista_responsavel_id: String(details.motorista_responsavel_id || ""),
        veiculo_responsavel_id: String(details.veiculo_responsavel_id || ""),
        funcionario_ids: (details.passageiros || []).map((passenger) => Number(passenger.funcionario_id)),
        observacoes: details.observacoes || "",
        is_teste: Boolean(details.is_teste),
      };
      setForm(next);
      formBaseline.current = JSON.stringify(next);
      setEditingId(Number(item.id));
      setEditingPlannedOrder(details.status === "planejada");
      setPassengerSearch("");
      setModalOpen(true);
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const togglePassenger = (id) => {
    const numericId = Number(id);
    setForm((current) => ({
      ...current,
      funcionario_ids: current.funcionario_ids.includes(numericId)
        ? current.funcionario_ids.filter((value) => value !== numericId)
        : [...current.funcionario_ids, numericId],
    }));
  };

  const unselectedVisiblePeople = filteredPeople.filter((person) => !form.funcionario_ids.includes(Number(person.id)));
  const markVisiblePeople = () => setForm((current) => ({
    ...current,
    funcionario_ids: [...new Set([...current.funcionario_ids, ...unselectedVisiblePeople.map((person) => Number(person.id))])],
  }));

  const save = async () => {
    if (!form.codigo.trim() || !form.data || !form.horario || !form.empresa_destino_id) {
      showMessage("Preencha código do pedido, empresa, data e horário.", "error");
      return;
    }
    if (!["ida", "volta"].includes(form.trecho)) {
      showMessage("Escolha o trecho: ida ou volta.", "error");
      return;
    }
    if (!form.funcionario_ids.length) {
      showMessage("Selecione ao menos um funcionário.", "error");
      return;
    }
    setSaving(true);
    try {
      const result = await apiFetch(editingId ? `/programacoes/${editingId}` : "/programacoes", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(form),
      });
      setModalOpen(false);
      if (result?.replanejamento_necessario) {
        showMessage("Pedido atualizado. Refaça o planejamento das vans.");
      } else {
        showMessage(editingId ? "Pedido atualizado." : "Pedido criado.");
      }
      setSelectedItemsById(current => { const next=new Map(current);if(editingId)next.delete(editingId);return next; });
      if (result?.replanejamento_necessario) {
        setMainTab("a_fazer");
        setSelectedDateKey(form.data);
        setDestinationFilter("");
        setLegFilter("");
        setSearch("");
        setQuery("");
        setPage(1);
        setHighlightIds(new Set([Number(editingId)]));
        setListRefresh(value => value + 1);
      }
      if (!editingId) { setMainTab('a_fazer');setSelectedDateKey(form.data);setDestinationFilter('');setLegFilter('');setSearch('');setQuery('');setPage(1); }
      await load(editingId ? page : 1);
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteItem) return;
    setSaving(true);
    try {
      await apiFetch(`/programacoes/${deleteItem.id}?cliente_id=${deleteItem.cliente_id}`, { method: "DELETE" });
      setSelectedItemsById((current) => {
        const next = new Map(current);
        next.delete(Number(deleteItem.id));
        return next;
      });
      setDeleteItem(null);
      showMessage("Pedido excluído.");
      await load(page);
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const withdrawPlanning = async () => {
    if (!withdrawItem) return;
    setSaving(true);
    try {
      await apiFetch(`/programacoes/${withdrawItem.id}/retirar-do-backlog`, {
        method: "POST",
        body: JSON.stringify({ cliente_id: withdrawItem.cliente_id }),
      });
      setWithdrawItem(null);
      showMessage("Planejamento desfeito. Os pedidos voltaram para A fazer.");
      setMainTab("a_fazer");
      setPage(1);
      setListRefresh(value => value + 1);
      await loadTabCounts(selectedDateKey);
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmPlanning = async (groupIds) => {
    setSaving(true);
    try {
      setPlanningDispatchProgress({});
      const result = await sendPlanningVans({ route: confirmDialog.rota, groupIds,
        onProgress: result => setPlanningDispatchProgress(previous => ({ ...previous, [result.routeId]: result })) });
      setConfirmDialog(null);
      const failed = result.results.some((item) => item.status === "failed");
      showMessage(planningDispatchMessage(result.results, result.planejamento.payload.grupos.length), failed ? "error" : "success");
      setLastDispatchRouteId(result.results[0]?.routeId || null);
      setMainTab(result.planejamento.payload.grupos.length ? "planejamento" : "operacao");
      if (!result.planejamento.payload.grupos.length) setBacklogRouteId(result.results[0]?.routeId || null);
      await loadTabCounts(selectedDateKey);
      await loadOperacaoCalendarCounts();
      await load(page);
    } catch (error) { showMessage(error.message, "error"); }
    finally { setSaving(false); }
  };

  const openConfirmDialog = async (item) => {
    if (!item?.rota_operacional_id) { showMessage("Salve o planejamento antes de enviar.", "error"); return; }
    setConfirmDialog({ item, rota: null, loading: true });
    try {
      const rota = await apiFetch(`/rotas/${item.rota_operacional_id}`);
      const initialGroupIds = (rota.payload.grupos || []).flatMap((group, index) => (
        group.motorista?.id && group.veiculo?.id
          ? [group.planejamento_grupo_id || `legacy-${rota.id}-${index}`]
          : []
      ));
      setConfirmDialog({ item, rota, initialGroupIds, loading: false });
    } catch (error) { setConfirmDialog(null); showMessage(error.message, "error"); }
  };

  const toggleSelection = (item) => {
    setSelectedItemsById((current) => {
      const next = new Map(current);
      const numericId = Number(item.id);
      if (next.has(numericId)) next.delete(numericId);
      else if (!programSelectionReason(item,[...current.values()])) next.set(numericId,{ ...item,id:numericId });
      return next;
    });
  };

  const openLotePlanning = async (pedidos) => {
    if (!pedidos?.length) return;
    return openSavedPlanning(pedidos[0].rota_operacional_id);
  };
  const openSavedPlanning = async (routeId, editPassengerGroupId = null, remember = true) => {
    const openingVersion = ++planningOpenVersion.current;
    setPlanning(true);
    try {
      const savedRoute = await apiFetch(`/rotas/${routeId}`);
      const allDetails = await Promise.all(
        savedRoute.payload.programacao_ids.map((id) => apiFetch(`/programacoes/${id}?cliente_id=${savedRoute.cliente_id}`))
      );
      if (openingVersion !== planningOpenVersion.current) return;
      if (remember) writeLocation({ montagem: routeId });
      openPlanning({ savedRoute, editPassengerGroupId, programs: allDetails, programacaoIds: savedRoute.payload.programacao_ids,
        pessoas: savedRoute.payload.grupos.flatMap((group) => group.pessoas), trecho: savedRoute.trecho,
        data: String(savedRoute.data).substring(0,10), horario: savedRoute.horario,
        empresaDestinoId: savedRoute.payload.empresa_destino_id, idPedido: allDetails.map(pg => pg.codigo).join(', '), isTeste: savedRoute.is_teste,
      });
    } catch (error) {
      if (openingVersion === planningOpenVersion.current) showMessage(error.message, "error");
    } finally {
      if (openingVersion === planningOpenVersion.current) setPlanning(false);
    }
  };

  openSavedPlanningRef.current = openSavedPlanning;

  const planSelected = async (items = selectedItems) => {
    if (!items.length) {
      showMessage("Selecione ao menos um pedido.", "error");
      return;
    }
    recordOperationalAction("selected_orders_planning_started", {
      count: items.length,
    });
    const openingVersion = ++planningOpenVersion.current;
    setPlanning(true);
    try {
      const details = [];
      for (const item of items) {
        const program = await apiFetch(`/programacoes/${item.id}?cliente_id=${item.cliente_id}`);
        if (program.status !== "rascunho") throw new Error(`O pedido ${program.codigo} já mudou de etapa. Atualize a lista antes de planejar.`);
        details.push(program);
      }
      if (openingVersion !== planningOpenVersion.current) return;
      const context = buildProgramPlanningContext(details);
      openPlanning(context);
      recordOperationalAction("selected_orders_planning_succeeded", {
        count: items.length,
      });
    } catch (error) {
      if (openingVersion !== planningOpenVersion.current) return;
      showMessage(error.message, "error");
      recordOperationalAction("selected_orders_planning_failed", {
        count: items.length,
        error_code: String(error?.code || "unknown"),
      });
    } finally {
      if (openingVersion === planningOpenVersion.current) setPlanning(false);
    }
  };

  if (workspace === "planejar") {
    return (
      <MontarRotaPage
        embedded
        pessoas={pessoas}
        motoristas={motoristas}
        veiculos={veiculos}
        draftRoute={draftRoute}
        onClearDraftRoute={() => {
          localStorage.removeItem(ROUTE_DRAFT_EDIT_STORAGE_KEY);
          setDraftRoute(null);
        }}
        programPlanningContext={planningContext}
        onClearProgramPlanning={() => setPlanningContext(null)}
        onBack={handlePlanningBack}
        onPlanningComplete={handlePlanningComplete}
        onEditProgram={(program) => { handlePlanningBack(); openEdit(program); }}
        onUndoPlanning={(program) => { handlePlanningBack(); setWithdrawItem(program); }}
      />
    );
  }

  return (
    <div className="fade-in rf-pedidos">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <PageTitle icon="orders">Pedidos</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 3, maxWidth: 720, lineHeight: 1.55 }}>
            Crie os pedidos, distribua os passageiros e acompanhe cada van.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {lastDispatchRouteId && <Btn variant="secondary" onClick={() => { setMainTab("operacao"); setBacklogRouteId(lastDispatchRouteId); }}>Acompanhar envios</Btn>}
          <Btn variant="secondary" onClick={() => load(page)}><Icon name="refresh" /> Atualizar</Btn>
          {mainTab !== "operacao" && (
            <Btn onClick={openNew}>+ Novo pedido</Btn>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <DateCalendarStrip
          countsByDate={activeCalendarCounts}
          selectedDateKey={selectedDateKey}
          onSelectDate={(dateKey) => { setSelectedDateKey(dateKey); setPage(1); }}
          calendarAnchor={calendarAnchor}
          onCalendarAnchorChange={setCalendarAnchor}
          isMobile={isMobile}
          countLabel={calendarCountLabel}
          countLabelPlural={calendarCountLabelPlural}
        />
      </div>

      <div className="rf-workspace-tabs" role="group" aria-label="Etapas dos pedidos" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {WORKSPACE_TABS.map((tab) => {
          const count = tabCounts[tab.id] ?? 0;
          return (
          <button
            key={tab.id}
            type="button"
            onClick={() => switchMainTab(tab.id)}
            aria-pressed={mainTab === tab.id}
            aria-label={`${tab.label}: ${count}`}

          >
            {tab.label}
            <span className="rf-tab-count">
              {count}
            </span>
          </button>
          );
        })}
      </div>

      <p style={{ color: "var(--muted)", fontSize: '0.9375rem', marginBottom: 16, lineHeight: 1.5 }}>
        {mainTab === "a_fazer"
          ? "Cada viagem junta os pedidos do mesmo destino, data, sentido e horário. Clique em Montar vans na viagem."
          : mainTab === "planejamento"
            ? "Viagens com vans montadas. Abra a montagem para ajustar ou envie aos motoristas quando quiser."
            : "Acompanhe os envios e as respostas dos motoristas por van."}
      </p>

      <div data-testid="active-pedidos-filter">
        <Card surface="filter"
          style={{
            marginBottom: 12,
            padding: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: '0.9375rem' }}>
            <span style={{ color: "var(--muted)", fontWeight: 700 }}>Filtro:</span>
            <span style={{ color: "var(--text)", fontWeight: 500 }}>{activeTab.label}</span>
            <span style={{ color: "var(--muted)" }}>·</span>
            <span style={{ color: "var(--text)", fontWeight: 700 }}>
              {selectedDateKey ? formatDate(selectedDateKey) : "Todas as datas"}
            </span>
            {activeSearch.trim() && (
              <>
                <span style={{ color: "var(--muted)" }}>·</span>
                <span style={{ color: "var(--text)" }}>Busca: “{activeSearch.trim()}”</span>
              </>
            )}
            {mainTab === "a_fazer" && selectedItems.length > 0 && (
              <>
                <span style={{ color: "var(--muted)" }}>·</span>
                <span
                  data-testid="selected-pedidos-summary"
                  style={{ color: "var(--accent)", fontWeight: 800 }}
                >
                  {selectedItems.length} {selectedItems.length === 1 ? "selecionado" : "selecionados"}
                  {hiddenSelectedCount > 0
                    ? ` · ${hiddenSelectedCount} fora desta busca/página`
                    : ""}
                </span>
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {activeSearch.trim() && (
              <Btn size="sm" variant="secondary" onClick={clearActiveSearch}>Limpar busca</Btn>
            )}
            {selectedDateKey && (
              <Btn size="sm" variant="secondary" onClick={() => { setSelectedDateKey(""); setPage(1); }}>
                Ver todas as datas
              </Btn>
            )}
          </div>
        </Card>
      </div>

      <Card surface="filter" className="rf-order-filters" style={{ marginBottom: 20 }}>
        {mainTab !== "operacao" && <><Select label="Empresa de destino" value={destinationFilter} onChange={event=>{setDestinationFilter(event.target.value);setPage(1);}}><option value="">Todos os destinos</option>{empresas.map(empresa=><option key={empresa.id} value={empresa.id}>{empresa.nome}</option>)}</Select><Select label="Sentido" value={legFilter} onChange={event=>{setLegFilter(event.target.value);setPage(1);}}><option value="">Ida e volta</option><option value="ida">Ida</option><option value="volta">Volta</option></Select></>}
        <Input label="Buscar pedidos"
          placeholder={mainTab === "operacao"
            ? "Buscar por código do pedido, motorista ou região..."
            : "Buscar por código do pedido ou motorista..."}
          value={mainTab === "operacao" ? operacaoSearch : search}
          onChange={(event) => {
            if (mainTab === "operacao") setOperacaoSearch(event.target.value);
            else setSearch(event.target.value);
          }}
          style={{ width: "100%" }}
        />
      </Card>

      {mainTab === "operacao" ? (
        <>
          <BacklogRotasPage
            key={operationLocationKey}
            embedded
            userRole={userRole}
            onEditRoute={handleEditRouteFromBacklog}
            onOpenDocument={onOpenDocuments}
            onOpenPlanning={openSavedPlanning}
            initialRouteId={backlogRouteId}
            onInitialRouteOpened={() => setBacklogRouteId(null)}
            sharedDateKey={selectedDateKey}
            onSharedDateChange={(dateKey) => setSelectedDateKey(dateKey)}
            sharedCalendarAnchor={calendarAnchor}
            onSharedCalendarAnchorChange={setCalendarAnchor}
            externalSearchQuery={operacaoQuery}
          />
        </>
      ) : loading ? (
        <Card style={{ padding: 40, textAlign: "center" }}><Spinner /></Card>
      ) : loadError ? (<div role="alert" className="planning-load-error"><p>{loadError}</p><Btn variant="secondary" onClick={()=>load(page)}>Tentar novamente</Btn></div>) : tabItems.length === 0 ? (
        <Card style={{ padding: 36, textAlign: "center", color: "var(--muted)" }}>
          <div style={{ color: "var(--text)", fontSize: '1rem', fontWeight: 800 }}>
            Nenhum resultado em “{activeTab.label}”
          </div>
          <div style={{ marginTop: 8, lineHeight: 1.55 }}>
            {mainTab === "a_fazer" ? "Cadastre ou importe pedidos para começar a montar as vans. " : "As viagens com vans montadas e ainda não enviadas aparecem aqui. "}
            {selectedDateKey
              ? `O filtro está limitado a ${formatDate(selectedDateKey)}.`
              : "A consulta está considerando todas as datas."}
            {activeSearch.trim() ? ` Busca ativa: “${activeSearch.trim()}”.` : ""}
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
            {WORKSPACE_TABS
              .filter((tab) => tab.id !== mainTab && Number(tabCounts[tab.id]) > 0)
              .map((tab) => (
                <Btn key={tab.id} size="sm" variant="secondary" onClick={() => switchMainTab(tab.id)}>
                  Ver {tab.label} ({tabCounts[tab.id]})
                </Btn>
              ))}
            {mainTab === "a_fazer" && !activeSearch.trim() && <Btn size="sm" onClick={openNew}>+ Novo pedido</Btn>}
            {mainTab === "planejamento" && <Btn size="sm" variant="secondary" onClick={() => switchMainTab("a_fazer")}>Ver pedidos para planejar</Btn>}
            {activeSearch.trim() && (
              <Btn size="sm" variant="secondary" onClick={clearActiveSearch}>Limpar busca</Btn>
            )}
            {selectedDateKey && (
              <Btn size="sm" variant="secondary" onClick={() => { setSelectedDateKey(""); setPage(1); }}>
                Ver todas as datas
              </Btn>
            )}
          </div>
        </Card>
      ) : mainTab === "planejamento" ? (
        <div style={{ display: "grid", gap: 12 }}>
          {loteGroups.map((group) => {
            const first = group.pedidos[0];
            const codigos = group.pedidos.map((pedido) => pedido.codigo).join(", ");
            const totalPassageiros = group.pedidos.reduce((sum, pedido) => sum + Number(pedido.total_passageiros || 0), 0);
            const planningRoute = summarizePlanningRoute(group.pedidos);
            const plannedPassengers = planningRoute.vans.length > 0 && planningRoute.vans.every(van => van.passageiros != null)
              ? planningRoute.vans.reduce((sum, van) => sum + Number(van.passageiros), 0) : null;
            const readyVanCount = planningRoute.vans.filter((van) => van.pronta).length;
            const incompleteVanCount = planningRoute.vans.filter((van) => !van.pronta).length;
            const expanded = expandedPlanning.has(group.key);
            const detailsId = `planning-details-${group.key}`;
            const canConfirmLote = readyVanCount > 0 && group.pedidos.some((pedido) => pedido.rota_operacional_id && pedido.rota_status === "planejamento");
            return (
              <Card key={group.key} className="planning-entry" style={{ padding: 16 }}>
                <div className="planning-entry-header">
                  <div className="planning-entry-summary rf-trip-summary">
                    <div className="rf-trip-time"><strong>{String(first.horario || '').slice(0, 5)}</strong><span>{first.trecho === "ida" ? "Ida" : "Volta"}</span></div>
                    <div className="rf-trip-info">
                      <strong>{first.empresa_destino_nome || "Destino não informado"}</strong>
                      <span>
                        {formatDate(first.data)}. {planningRoute.vanCount > 0 ? `${planningRoute.vanCount} ${planningRoute.vanCount === 1 ? 'van montada' : 'vans montadas'}` : 'Quantidade de vans indisponível'}, {plannedPassengers != null
                          ? `${plannedPassengers} ${plannedPassengers === 1 ? 'passageiro' : 'passageiros'}`
                          : `${totalPassageiros} passageiros`}
                        {incompleteVanCount > 0 && `. ${incompleteVanCount} ${incompleteVanCount === 1 ? 'van sem motorista ou veículo' : 'vans sem motorista ou veículo'}`}
                      </span>
                      <span className="rf-trip-orders">{group.pedidos.length === 1 ? "Pedido" : "Pedidos"} {codigos}</span>
                    </div>
                  </div>
                  <div className="planning-entry-actions">
                    <Btn size="sm" href={locationHref({montagem:first.rota_operacional_id})} onClick={() => openLotePlanning(group.pedidos)} disabled={planning}>
                      Abrir montagem
                    </Btn>
                    {canConfirmLote && (
                      <Btn size="sm" variant="secondary" onClick={() => openConfirmDialog(first)} disabled={saving || confirmDialog?.loading}>
                        {readyVanCount === 1 ? "Enviar ao motorista (1 van pronta)" : `Enviar aos motoristas (${readyVanCount} vans prontas)`}
                      </Btn>
                    )}
                  </div>
                </div>
                <Btn size="sm" variant="secondary" className="planning-entry-toggle"
                  aria-expanded={expanded} aria-controls={detailsId}
                  aria-label={`${expanded ? 'Recolher detalhes' : 'Ver vans e pedidos'} de ${group.pedidos.length === 1 ? 'PG' : 'PGs'} ${codigos}`}
                  onClick={() => togglePlanningDetails(group.key)}>
                  <Icon name="chevron-down" style={{ transform: expanded ? 'rotate(180deg)' : undefined }} />
                  {expanded ? 'Recolher detalhes' : 'Ver vans e pedidos'}
                </Btn>
                <div id={detailsId} hidden={!expanded}>
                {expanded && <>
                {planningRoute.vans.length > 0 && (
                  <ul className="planning-entry-vans" aria-label="Vans pendentes do planejamento">
                    {planningRoute.vans.map((van) => (
                      <li key={van.groupId || van.index}>
                        <div className="planning-entry-van-identity">
                          <strong>Van {van.numero || van.index + 1}</strong>
                          <span>{[van.regiao, van.passageiros != null ? `${van.passageiros} ${van.passageiros === 1 ? 'passageiro' : 'passageiros'}` : null].filter(Boolean).join(' · ')}</span>
                        </div>
                        <div className="planning-entry-van-executors">
                          <span><span className="planning-entry-field">Motorista</span>{van.motorista || 'Não definido'}</span>
                          <span><span className="planning-entry-field">Veículo</span>{van.veiculo || 'Não definido'}</span>
                        </div>
                        <span className={`planning-van-status${van.pronta && van.conferida ? ' is-reviewed' : ''}`}>
                          {van.pronta ? (van.conferida ? 'Conferida' : 'Falta conferir') : 'Faltam dados'}
                        </span>
                        {van.groupId && <Btn size="sm" variant="secondary" disabled={planning} aria-label={`Editar passageiros da van ${van.numero || van.index + 1}`} onClick={() => openSavedPlanning(first.rota_operacional_id, van.groupId)}>Editar passageiros</Btn>}
                      </li>
                    ))}
                  </ul>
                )}
                <PlanningOrders programs={group.pedidos} busy={saving || planning}
                  onEdit={openEdit} onUndo={setWithdrawItem} onDelete={setDeleteItem} />
                </>}
                </div>
              </Card>
            );
          })}
          <Pagination page={page} total={total} perPage={PER_PAGE} onChange={setPage} />
        </div>
      ) : (
        <div className="rf-order-list">
          {tripsToBuild.length > 0 && <section className="rf-trips" aria-labelledby="rf-trips-title">
            <h2 id="rf-trips-title">Viagens para montar</h2>
            <p className="planning-help">Para juntar pedidos com horários diferentes (até 1 hora), marque-os na tabela abaixo.</p>
            {tripsToBuild.map((trip) => <div key={trip.key} className="rf-trip">
              <div className="rf-trip-time"><strong>{String(trip.first.horario || '').slice(0, 5)}</strong><span>{trip.first.trecho === 'ida' ? 'Ida' : 'Volta'}</span></div>
              <div className="rf-trip-info"><strong>{trip.first.empresa_destino_nome || 'Destino não informado'}</strong>
                <span>{formatDate(trip.first.data)}. {trip.items.length} {trip.items.length === 1 ? 'pedido' : 'pedidos'}, {trip.passengers} {trip.passengers === 1 ? 'passageiro' : 'passageiros'}</span></div>
              <Btn size="sm" disabled={planning || saving || loading} onClick={() => planSelected(trip.items)}>Montar vans</Btn>
            </div>)}
          </section>}
          <div className="planning-section-top"><p className="planning-help">{total} {total===1?'pedido a planejar':'pedidos a planejar'} neste filtro</p><Btn variant="secondary" size="sm" disabled={!selectedItems.length || planning || saving || loading || search.trim()!==query.trim()} onClick={()=>setSelectedItemsById(previous=>{const next=new Map(previous);for(const item of tabItems){if(!programSelectionReason(item,[...next.values()]))next.set(Number(item.id),{...item,id:Number(item.id)});}return next;})}>Selecionar compatíveis desta página</Btn></div>
          <OrderSelectionTable items={tabItems} selected={selectedItems} onToggle={toggleSelection} onEdit={openEdit} onDelete={setDeleteItem} highlighted={highlightIds} sourceLabel={item=>getPedidoOrigemLabel(config,item.origem_integracao || item.fonte)} busy={planning || saving || loading || search.trim()!==query.trim()}/>
          <Pagination page={page} total={total} perPage={PER_PAGE} onChange={setPage} />
        </div>
      )}

      {mainTab === 'a_fazer' && selectedItems.length > 0 && <div className="planning-toolbar rf-order-toolbar"><div className="planning-toolbar-summary" aria-live="polite"><strong>{selectedItems.length ? `${selectedItems.length} ${selectedItems.length===1?'pedido selecionado':'pedidos selecionados'} · ${selectedItems.reduce((sum,item)=>sum+Number(item.total_passageiros||0),0)} passageiros` : 'Selecione os pedidos que deseja planejar juntos'}</strong><span>{selectedItems.length ? `${selectedItems[0].empresa_destino_nome || 'Mesmo destino'} · ${formatDate(selectedItems[0].data)} · ${selectedItems[0].trecho==='ida'?'Ida':'Volta'}${hiddenSelectedCount?` · ${hiddenSelectedCount} fora desta busca/página`:''}` : 'Mesmo destino, data e sentido · intervalo de até 1 hora'}</span></div><Btn variant="secondary" disabled={planning || !selectedItems.length} onClick={()=>setSelectedItemsById(new Map())}>Limpar seleção</Btn><Btn onClick={() => planSelected()} disabled={planning || !selectedItems.length}>{planning ? <><Spinner/> Preparando…</> : `Montar vans dos selecionados (${selectedItems.length})`}</Btn></div>}

      {modalOpen && (
        <Modal title={editingId ? "Editar pedido" : "Novo pedido"} onClose={closeForm} maxWidth={640}>
          <Form values={form} onSubmit={save} busy={saving} protect={false} className="rf-order-form" style={{ display: "grid", gap: 16 }}>
            {editingPlannedOrder && <p className="planning-edit-warning">Alterar passageiros, data, trecho ou horário devolve todos os pedidos deste planejamento para A fazer. Será necessário montar as vans novamente.</p>}
            {!editingId && <p className="planning-help">O pedido fica em A fazer. Motorista, veículo e distribuição podem ser definidos no planejamento.</p>}
            <Input label="Código do pedido *" value={form.codigo} onChange={(event) => setForm({ ...form, codigo: event.target.value })} placeholder="2026/46635" />
            <Select label="Empresa de destino *" value={form.empresa_destino_id} onChange={(event) => setForm({ ...form, empresa_destino_id: event.target.value, motorista_responsavel_id: "", veiculo_responsavel_id: "", funcionario_ids: [] })}>
              <option value="">Selecione</option>
              {empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>)}
            </Select>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Input label="Data *" type="date" value={form.data} onChange={(event) => setForm({ ...form, data: event.target.value })} />
              <Input label="Horário *" type="time" value={form.horario} onChange={(event) => setForm({ ...form, horario: event.target.value })} />
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginBottom: 7 }}>Trecho</div>
              <TrechoToggle trecho={form.trecho} setTrecho={(value) => setForm({ ...form, trecho: value })} />
            </div>
            <details><summary className="rf-optional-executors">Motorista e veículo sugeridos · opcional</summary><Select label="Motorista responsável" value={form.motorista_responsavel_id} onChange={(event) => setForm({ ...form, motorista_responsavel_id: event.target.value })}>
              <option value="">Definir depois</option>
              {availableDrivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.nome}</option>)}
            </Select>
            <Select label="Veículo responsável" value={form.veiculo_responsavel_id} onChange={(event) => setForm({ ...form, veiculo_responsavel_id: event.target.value })}>
              <option value="">Definir depois</option>
              {availableVehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.veiculo} {vehicle.placa ? `· ${vehicle.placa}` : ""} · {vehicle.capacidade || 15} lugares</option>)}
            </Select>
            </details><label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: '0.9375rem', cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_teste} onChange={(event) => setForm({ ...form, is_teste: event.target.checked })} style={{ accentColor: "#f97316" }} />
              Pedido de teste
            </label>
            <Input label="Buscar passageiros" value={passengerSearch} onChange={(event) => setPassengerSearch(event.target.value)} placeholder="Nome, bairro ou região" />
            {unselectedVisiblePeople.length > 1 && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>{unselectedVisiblePeople.length} {passengerSearch.trim() ? "encontrados ainda não marcados" : "listados ainda não marcados"}</span>
              <Btn size="sm" variant="secondary" onClick={markVisiblePeople}>Marcar os {unselectedVisiblePeople.length}</Btn>
            </div>}
            <div style={{ maxHeight: 230, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 10 }}>
              {filteredPeople.map((person) => {
                const isSelected = form.funcionario_ids.includes(Number(person.id));
                return (
                  <label
                    key={person.id}
                    data-selected={isSelected ? "true" : "false"}
                    style={{
                      display: "flex",
                      gap: 9,
                      alignItems: "center",
                      padding: "9px 11px",
                      borderBottom: "1px solid var(--border)",
                      background: isSelected ? "rgba(249,115,22,.10)" : "transparent",
                      cursor: "pointer",
                      fontSize: '0.9375rem',
                    }}
                  >
                    <input type="checkbox" checked={isSelected} onChange={() => togglePassenger(person.id)} style={{ accentColor: "#f97316" }} />
                    <span style={{ flex: 1 }}>{person.nome}</span>
                    <span style={{ color: "var(--muted)", fontSize: '0.8125rem' }}>{person.regiao || person.bairro || person.cidade || "—"}</span>
                  </label>
                );
              })}
              {!filteredPeople.length && <div style={{ padding: 20, textAlign: "center", color: "var(--muted)", fontSize: '0.9375rem' }}>Nenhum funcionário disponível.</div>}
            </div>
            <div style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
              {form.funcionario_ids.length} {form.funcionario_ids.length===1?"passageiro selecionado":"passageiros selecionados"}
              {form.funcionario_ids.length > 0 ? " · selecionados aparecem primeiro" : ""}
            </div>
            <div className="rf-form-actions"><Btn variant="secondary" onClick={closeForm} disabled={saving}>Cancelar</Btn>
              <Btn type="submit" disabled={saving} >{saving ? <Spinner /> : editingId ? "Salvar alterações" : "Criar pedido"}</Btn>

            </div>
          </Form>
        </Modal>
      )}

      <ConfirmModal open={Boolean(formDiscard)} title="Descartar alterações no pedido?" message="O preenchimento ainda não foi salvo." confirmLabel="Descartar alterações" cancelLabel="Continuar editando" onClose={()=>setFormDiscard(null)} onConfirm={()=>{const action=formDiscard.run;setFormDiscard(null);action();}} />

      <ConfirmModal
        open={Boolean(deleteItem)}
        title="Excluir pedido"
        message={`Excluir definitivamente o pedido ${deleteItem?.codigo || ""}?`}
        confirmLabel="Excluir"
        confirmVariant="danger"
        onClose={() => setDeleteItem(null)}
        onConfirm={remove}
      />

      <ConfirmModal
        open={Boolean(withdrawItem)}
        title="Desfazer planejamento"
        message={`Desfazer o planejamento do pedido ${withdrawItem?.codigo || ""}? Todos os pedidos deste grupo voltarão para A fazer. Os pedidos e passageiros serão preservados.`}
        confirmLabel="Desfazer"
        confirmVariant="danger"
        onClose={() => setWithdrawItem(null)}
        onConfirm={withdrawPlanning}
      />

      {confirmDialog?.loading && <Modal title="Carregando planejamento" onClose={() => setConfirmDialog(null)}><Spinner /></Modal>}
      {confirmDialog?.rota && <PlanningDispatchModal route={confirmDialog.rota} initialGroupIds={confirmDialog.initialGroupIds} progress={planningDispatchProgress}
        destination={empresas.find(empresa => Number(empresa.id) === Number(confirmDialog.rota.payload?.empresa_destino_id))?.nome}
        loading={saving} onClose={() => setConfirmDialog(null)} onConfirm={confirmPlanning} />}

      {toast && (
<Toast onDismiss={() => setToast(null)} tone={toast.type === "error" ? "error" : "success"}>{toast.message}</Toast>
      )}
    </div>
  );
}
